import React from 'react';
import PropTypes from 'prop-types';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import CloudOffOutlinedIcon from '@mui/icons-material/CloudOffOutlined';
import { EmptyState } from './style/cards/_PdiHelpers';

// ---------------------------------------------------------------------------
// Shared "this webshop can't be shown" surfaces for the webshop screens.
//
// Two scenarios the admin must make obvious instead of rendering an empty,
// editable-looking screen:
//   1. No webshop is linked to the account (no lic_clients_webshop row) →
//      `<NoWebshopState>`.
//   2. A webshop row exists but the mini-api call failed (wrong credentials,
//      not deployed, offline, inactive, …) → `<WebshopErrorState>`, which
//      maps the auth-proxy error code to a plain-language Dutch reason.
//
// Per the product decision these are MESSAGE-ONLY (no retry/connect buttons).
// ---------------------------------------------------------------------------

// Auth-proxy error codes (the `translation_key` on a {success:false} result)
// → [i18n key, Vlaamse fallback]. Keep the fallback usable on its own so the
// message reads correctly even before the locale files are regenerated.
const ERROR_COPY = {
    mini_api_unreachable:   ['cfg_webshop_err_unreachable',  'De webshop is niet bereikbaar. Controleer of de mini-api is uitgerold en of de URL en inloggegevens kloppen.'],
    webshop_not_active:     ['cfg_webshop_err_inactive',     'Deze webshop staat op niet-actief. Activeer ze om de instellingen te kunnen beheren.'],
    webshop_not_found:      ['cfg_webshop_err_notfound',     'Deze webshop werd niet gevonden in de licentiedatabank.'],
    webshop_not_configured: ['cfg_webshop_err_nourl',        'Voor deze webshop is nog geen mini-api-URL ingesteld.'],
    error_network:          ['cfg_webshop_err_network',      'Geen netwerkverbinding met de server. Probeer het later opnieuw.'],
    error_timeout:          ['cfg_webshop_err_timeout',      'De verbinding met de webshop verliep te traag (time-out).'],
    error_server:           ['cfg_webshop_err_server',       'De server gaf een onverwachte fout terug.'],
};

/**
 * Resolve a proxy error into a human reason. Accepts either a {message, code}
 * object, a raw {success:false, translation_key, message} proxy result, or a
 * plain string. Falls back to the raw message, then a generic line.
 */
export const webshopErrorText = (err, t) => {
    if (!err) return '';
    const code = typeof err === 'string' ? null : (err.code || err.translation_key);
    const entry = code ? ERROR_COPY[code] : null;
    if (entry) return t ? t(entry[0], entry[1]) : entry[1];
    const msg = typeof err === 'string' ? err : err.message;
    return msg || (t ? t('cfg_webshop_err_generic', 'Er ging iets mis bij het laden van de webshop.') : 'Er ging iets mis bij het laden van de webshop.');
};

/** Scenario 1 — no webshop linked to the account. */
export const NoWebshopState = ({ t }) => (
    <EmptyState
        icon={<StorefrontOutlinedIcon />}
        title={t ? t('cfg_no_webshop_title', 'Geen webshop gekoppeld') : 'Geen webshop gekoppeld'}
        description={t
            ? t('cfg_no_webshop_desc', 'Er is nog geen webshop aan dit account gekoppeld. Neem contact op met SoftTouch om je webshop te activeren.')
            : 'Er is nog geen webshop aan dit account gekoppeld. Neem contact op met SoftTouch om je webshop te activeren.'}
    />
);
NoWebshopState.propTypes = { t: PropTypes.func };

/** Scenario 2 — webshop exists but the mini-api could not be reached. */
export const WebshopErrorState = ({ error, t }) => (
    <EmptyState
        icon={<CloudOffOutlinedIcon />}
        title={t ? t('cfg_webshop_err_title', 'Webshop niet bereikbaar') : 'Webshop niet bereikbaar'}
        description={webshopErrorText(error, t)}
    />
);
WebshopErrorState.propTypes = { error: PropTypes.oneOfType([PropTypes.object, PropTypes.string]), t: PropTypes.func };
